import { useCallback, useEffect, useState } from "react";
import AdminShell from "@/components/feature/AdminShell";
import {
  fetchNotificationsOverview,
  fetchNotificationSettings,
  saveNotificationSettings,
  type NotificationSettings,
  type NotificationsOverview,
} from "@/services/whatsapp";
import { timeAgo } from "@/lib/timeAgo";

const inputCls = "w-full bg-background-100 border border-background-200 rounded-xl px-3.5 py-2.5 text-sm outline-none focus:border-primary-400";
const BRIDGE_URL = "https://bmvmctziknmhupgwertf.supabase.co/functions/v1/wa-bridge";

const KIND_LABEL: Record<string, string> = {
  reminder_24h: "Recordatorio (24 h)",
  reminder_1h: "Recordatorio (1 h)",
  event_cancelled: "Evento cancelado",
};

const STATUS_STYLE: Record<string, string> = {
  pending: "bg-amber-500/15 text-amber-700",
  sending: "bg-amber-500/15 text-amber-700",
  sent: "bg-primary-500/15 text-primary-700",
  failed: "bg-accent-500/15 text-accent-700",
};
const STATUS_LABEL: Record<string, string> = { pending: "En cola", sending: "Enviando", sent: "Enviado", failed: "Falló" };

const PLACEHOLDERS = "{nombre} {titulo} {fecha} {hora} {enlace}";

export default function AdminNotificaciones() {
  const [settings, setSettings] = useState<NotificationSettings | null>(null);
  const [overview, setOverview] = useState<NotificationsOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const showToast = (m: string) => {
    setToast(m);
    window.setTimeout(() => setToast(null), 2400);
  };

  const load = useCallback(async () => {
    const [s, o] = await Promise.all([fetchNotificationSettings(), fetchNotificationsOverview()]);
    setSettings(s);
    setOverview(o);
  }, []);

  useEffect(() => {
    let active = true;
    load()
      .catch((e) => active && setError(e instanceof Error ? e.message : "No se pudo cargar la configuración."))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [load]);

  const save = async () => {
    if (!settings) return;
    const number = settings.whatsappNumber.replace(/\D/g, "");
    if (number && !/^[0-9]{8,15}$/.test(number)) return setFormError("El número debe tener entre 8 y 15 dígitos, con código de país y sin signos (ej. 573001234567).");
    for (const [label, t] of [
      ["24 h", settings.template24h],
      ["1 h", settings.template1h],
      ["cancelación", settings.templateCancel],
    ] as const) {
      if (t.trim().length < 10) return setFormError(`El mensaje de ${label} es muy corto.`);
    }
    setFormError(null);
    setSaving(true);
    try {
      await saveNotificationSettings({ ...settings, whatsappNumber: number });
      await load();
      showToast("Configuración guardada");
    } catch {
      setFormError("No se pudo guardar. Intenta de nuevo.");
    } finally {
      setSaving(false);
    }
  };

  const set = <K extends keyof NotificationSettings>(k: K, v: NotificationSettings[K]) => setSettings((s) => (s ? { ...s, [k]: v } : s));

  return (
    <AdminShell title="Avisos por WhatsApp" description="Recordatorios de reuniones y eventos enviados por WhatsApp a quienes los activan. El envío lo hace n8n; aquí defines el número y los mensajes.">
      {loading ? (
        <div className="bg-background-100 border border-background-200 rounded-2xl p-10 text-center text-foreground-500">
          <i className="ri-loader-4-line animate-spin text-2xl"></i>
        </div>
      ) : error || !settings ? (
        <div className="border border-dashed border-accent-400 rounded-2xl p-10 text-center text-accent-700">{error ?? "No hay configuración para tu organización."}</div>
      ) : (
        <div className="space-y-5">
          {overview ? (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              {[
                { l: "Personas con avisos activos", v: overview.linked, i: "ri-whatsapp-line" },
                { l: "En cola", v: overview.pending, i: "ri-time-line" },
                { l: "Enviados (7 días)", v: overview.sent7d, i: "ri-send-plane-line" },
                { l: "Fallidos (7 días)", v: overview.failed7d, i: "ri-error-warning-line" },
              ].map((c) => (
                <div key={c.l} className="bg-background-100 border border-background-200 rounded-2xl p-4">
                  <i className={`${c.i} text-primary-600 text-lg`}></i>
                  <div className="font-heading font-extrabold text-2xl text-foreground-950 mt-1">{c.v}</div>
                  <div className="text-xs text-foreground-500">{c.l}</div>
                </div>
              ))}
            </div>
          ) : null}

          <section className="bg-background-100 border border-background-200 rounded-2xl p-5 space-y-4">
            <h2 className="font-heading font-bold text-foreground-950">Configuración</h2>
            <label className="block text-xs font-semibold text-foreground-600">
              Número de WhatsApp de Vívelo (con código de país, solo dígitos)
              <input value={settings.whatsappNumber} onChange={(e) => set("whatsappNumber", e.target.value.replace(/\D/g, ""))} placeholder="573001234567" inputMode="numeric" className={`${inputCls} mt-1`} />
              <span className="block mt-1 font-normal text-foreground-500">Es el número de WhatsApp Business que usa n8n. Mientras esté vacío, la app no ofrece activar los avisos.</span>
            </label>
            <div className="flex flex-wrap gap-5">
              <label className="flex items-center gap-2 text-sm text-foreground-700 cursor-pointer">
                <input type="checkbox" checked={settings.remind24h} onChange={(e) => set("remind24h", e.target.checked)} className="w-4 h-4 accent-primary-500" />
                Recordatorio 24 h antes
              </label>
              <label className="flex items-center gap-2 text-sm text-foreground-700 cursor-pointer">
                <input type="checkbox" checked={settings.remind1h} onChange={(e) => set("remind1h", e.target.checked)} className="w-4 h-4 accent-primary-500" />
                Recordatorio 1 h antes
              </label>
            </div>
            <p className="text-xs text-foreground-500">Los avisos solo salen entre las 7:00 y las 21:59 de la zona horaria de cada persona.</p>

            {(
              [
                ["template24h", "Mensaje de recordatorio (24 h)"],
                ["template1h", "Mensaje de recordatorio (1 h)"],
                ["templateCancel", "Mensaje de cancelación"],
              ] as const
            ).map(([key, label]) => (
              <label key={key} className="block text-xs font-semibold text-foreground-600">
                {label}
                <textarea value={settings[key]} onChange={(e) => set(key, e.target.value)} rows={4} maxLength={900} className={`${inputCls} mt-1 font-normal`} />
              </label>
            ))}
            <p className="text-xs text-foreground-500">
              Puedes usar: <code className="bg-background-200 rounded px-1">{PLACEHOLDERS}</code>. Evita promesas de resultados o ingresos.
            </p>
            {formError ? <p className="text-sm text-accent-700">{formError}</p> : null}
            <div className="flex justify-end">
              <button onClick={save} disabled={saving} className="px-5 py-2 rounded-full text-sm font-semibold bg-primary-500 text-background-950 hover:bg-primary-600 disabled:opacity-50 cursor-pointer">
                {saving ? "Guardando…" : "Guardar"}
              </button>
            </div>
          </section>

          <section className="bg-background-100 border border-background-200 rounded-2xl p-5">
            <h2 className="font-heading font-bold text-foreground-950">Conexión con n8n</h2>
            <ol className="mt-2 list-decimal pl-5 space-y-1.5 text-sm text-foreground-700">
              <li>
                En Supabase → Edge Functions → Secrets crea <code className="bg-background-200 rounded px-1">WA_BRIDGE_SECRET</code> con una clave larga y aleatoria.
              </li>
              <li>
                En n8n crea una credencial «Header Auth» llamada «Vivelo WA Bridge» con nombre <code className="bg-background-200 rounded px-1">x-bridge-secret</code> y esa misma clave.
              </li>
              <li>Importa los dos workflows de la carpeta n8n del proyecto y conecta tus credenciales de WhatsApp Business.</li>
              <li>Activa ambos workflows. Los avisos se envían cada 5 minutos.</li>
            </ol>
            <div className="mt-3 text-xs text-foreground-500 break-all">
              Dirección del puente: <code className="bg-background-200 rounded px-1">{BRIDGE_URL}</code>
            </div>
          </section>

          <section className="bg-background-100 border border-background-200 rounded-2xl overflow-hidden">
            <div className="p-5 pb-3 flex items-center justify-between">
              <h2 className="font-heading font-bold text-foreground-950">Últimos avisos</h2>
              <button onClick={() => load()} className="text-xs font-semibold text-primary-700 cursor-pointer">
                <i className="ri-refresh-line mr-1"></i>Actualizar
              </button>
            </div>
            {!overview || overview.log.length === 0 ? (
              <p className="px-5 pb-6 text-sm text-foreground-500">Todavía no se ha generado ningún aviso.</p>
            ) : (
              <ul className="divide-y divide-background-200">
                {overview.log.map((r) => (
                  <li key={r.id} className="px-5 py-3 flex flex-col md:flex-row md:items-center gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-foreground-950 truncate">
                        {KIND_LABEL[r.kind] ?? r.kind} · {r.event_title ?? "—"}
                      </div>
                      <div className="text-xs text-foreground-500">
                        {r.user_name ?? "Sin nombre"} · …{r.phone_last4} · {timeAgo(r.created_at)}
                        {r.error ? ` · ${r.error}` : ""}
                      </div>
                    </div>
                    <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full self-start md:self-auto ${STATUS_STYLE[r.status] ?? ""}`}>{STATUS_LABEL[r.status] ?? r.status}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      )}
      {toast ? <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-background-950 text-foreground-50 text-sm font-semibold px-4 py-2.5 rounded-full shadow-lg">{toast}</div> : null}
    </AdminShell>
  );
}
